import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import PageLayout from "@/components/layout/PageLayout";
import { Section } from "@/components/layout/Sections";
import ContactForm from "@/components/ContactForm";
import api, { mediaUrl } from "@/lib/api";
import {
  Calendar, MapPin, Link as LinkIcon, Download,
  ChevronLeft, ChevronRight, X, Images, FileText,
} from "lucide-react";

export default function EventoDetail() {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxIdx, setLightboxIdx] = useState(-1);
  const [showPressRelease, setShowPressRelease] = useState(false);

  useEffect(() => {
    api.get(`/events/${slug}`)
      .then((r) => setEvent(r.data))
      .catch(() => setEvent(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const copyEventLink = async () => {
    const url = window.location.href.split("#")[0];
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link evento copiato");
    } catch {
      toast.error(`Copia manualmente: ${url}`);
    }
  };

  const copyGalleryLink = async () => {
    const base = window.location.href.split("#")[0];
    const url = `${base}#gallery`;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link alla gallery copiato");
    } catch {
      toast.error(`Copia manualmente: ${url}`);
    }
  };

  const gallery = event?.gallery || [];

  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(-1);
  const prevImg = useCallback(() => setLightboxIdx((i) => (i > 0 ? i - 1 : gallery.length - 1)), [gallery.length]);
  const nextImg = useCallback(() => setLightboxIdx((i) => (i < gallery.length - 1 ? i + 1 : 0)), [gallery.length]);

  // Keyboard nav for lightbox
  useEffect(() => {
    if (lightboxIdx < 0) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
      else if (e.key === "ArrowLeft") prevImg();
      else if (e.key === "ArrowRight") nextImg();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightboxIdx, prevImg, nextImg]);

  const downloadImage = async (url) => {
    try {
      const resolved = mediaUrl(url);
      const r = await fetch(resolved);
      const blob = await r.blob();
      const link = document.createElement("a");
      const name = url.split("/").pop()?.split("?")[0] || "foto-evento.jpg";
      link.href = URL.createObjectURL(blob);
      link.download = name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success("Download avviato");
    } catch {
      toast.error("Download fallito, prova a tenere premuto sulla foto per salvarla");
    }
  };

  if (loading) {
    return <PageLayout><Section><p className="text-gray-500 italic">Caricamento evento…</p></Section></PageLayout>;
  }
  if (!event) {
    return (
      <PageLayout>
        <Section>
          <h1 className="font-display uppercase text-3xl">Evento non trovato</h1>
          <p className="mt-4"><Link to="/moto/eventi" className="text-[color:var(--nsa-orange)]">← Torna agli eventi</Link></p>
        </Section>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      {/* HERO */}
      <section className="relative min-h-[65vh] md:min-h-[85vh] w-full overflow-hidden bg-gray-900">
        {event.cover_image ? (
          <div
            className="absolute inset-0 bg-cover bg-center scale-105"
            style={{ backgroundImage: `url(${mediaUrl(event.cover_image)})` }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: "radial-gradient(circle at 30% 40%, rgba(255,102,0,0.18) 0%, transparent 60%), linear-gradient(180deg, #111 0%, #000 100%)" }}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/90" />
        <div className="grain absolute inset-0" />

        <div className="relative nsa-container h-full min-h-[65vh] md:min-h-[85vh] flex flex-col justify-end pb-14 md:pb-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="label-eyebrow text-[color:var(--nsa-orange)] tracking-[0.3em] text-xs md:text-sm">
              {event.is_past ? "Evento concluso · Grazie a tutti" : "Evento NSA Motors"}
            </span>
            <h1 className="font-display uppercase text-white text-3xl md:text-6xl leading-[0.95] mt-3 drop-shadow-2xl">{event.title}</h1>
            <div className="mt-7 flex flex-wrap gap-3 md:gap-5 text-white">
              {event.date_label && (
                <div className="flex items-center gap-2 border border-white/30 px-3 md:px-4 py-2 backdrop-blur-sm bg-black/30">
                  <Calendar size={14} className="text-[color:var(--nsa-orange)]" />
                  <span className="label-eyebrow tracking-[0.2em] text-xs md:text-sm">{event.date_label}</span>
                </div>
              )}
              {event.location && (
                <div className="flex items-center gap-2 border border-white/30 px-3 md:px-4 py-2 backdrop-blur-sm bg-black/30">
                  <MapPin size={14} className="text-[color:var(--nsa-orange)]" />
                  <span className="label-eyebrow tracking-[0.2em] text-xs md:text-sm">{event.location}</span>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Corpo editoriale */}
      <Section>
        <div className="grid md:grid-cols-3 gap-10">
          <div className="md:col-span-2">
            <div className="prose prose-lg max-w-none event-body">
              <ReactMarkdown
                components={{
                  h2: (p) => <h2 className="font-display uppercase text-2xl md:text-3xl mt-10 mb-4" {...p} />,
                  h3: (p) => <h3 className="font-display uppercase text-xl md:text-2xl mt-8 mb-3 text-[color:var(--nsa-orange)]" {...p} />,
                  p: (p) => <p className="text-lg mb-5 text-gray-700 leading-relaxed" {...p} />,
                  strong: (p) => <strong className="text-gray-900 font-semibold" {...p} />,
                  ul: (p) => <ul className="my-5 space-y-2 pl-0" {...p} />,
                  li: (p) => <li className="pl-5 relative text-gray-700 before:content-['→'] before:absolute before:left-0 before:text-[color:var(--nsa-orange)] before:font-bold" {...p} />,
                  img: ({ src, alt }) => (
                    <figure className="my-8">
                      <img src={mediaUrl(src)} alt={alt} className="w-full h-auto" loading="lazy" />
                      {alt && <figcaption className="mt-2 text-sm text-gray-500 italic text-center">{alt}</figcaption>}
                    </figure>
                  ),
                }}
              >
                {event.body || ""}
              </ReactMarkdown>
            </div>
          </div>

          <div className="md:col-span-1">
            {event.is_past ? (
              <div className="card-flat p-6 bg-[color:var(--nsa-orange)]/5 border-l-4 border-[color:var(--nsa-orange)] md:sticky md:top-24" data-testid="past-event-sidebar">
                <div className="flex items-center gap-2 text-[color:var(--nsa-orange)]">
                  <FileText size={16} />
                  <span className="label-eyebrow tracking-[0.2em] text-xs">Comunicato stampa</span>
                </div>
                <h3 className="font-display uppercase text-xl mt-3 leading-tight">Leggi il comunicato</h3>
                {event.press_release && (
                  <p className="text-sm text-gray-700 mt-3 leading-relaxed line-clamp-5">
                    {event.press_release
                      .replace(/^#+\s.*$/gm, "")   // strip headings
                      .replace(/[*_>#]/g, "")       // strip md markers
                      .replace(/\s+/g, " ")
                      .trim()
                      .slice(0, 240)}…
                  </p>
                )}
                {event.press_release && (
                  <button
                    onClick={() => setShowPressRelease(true)}
                    className="mt-5 w-full btn-orange label-eyebrow text-center py-3 inline-flex items-center justify-center gap-2"
                    data-testid="btn-leggi-comunicato"
                  >
                    <FileText size={14} /> Leggi tutto il comunicato
                  </button>
                )}
                {gallery.length > 0 && (
                  <a href="#gallery" className="mt-3 block w-full border border-gray-900 hover:bg-gray-900 hover:text-white px-4 py-3 label-eyebrow text-sm text-center transition-colors inline-flex items-center gap-2 justify-center" data-testid="btn-vai-gallery">
                    <Images size={14} /> Vai alla gallery
                  </a>
                )}
                <div className="mt-5 pt-5 border-t border-gray-200 space-y-2">
                  <button
                    onClick={copyEventLink}
                    className="w-full border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-2.5 label-eyebrow text-xs transition-colors inline-flex items-center gap-2 justify-center"
                    data-testid="btn-copy-event-link"
                  >
                    <LinkIcon size={13} /> Copia link evento
                  </button>
                  {gallery.length > 0 && (
                    <button
                      onClick={copyGalleryLink}
                      className="w-full border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-2.5 label-eyebrow text-xs transition-colors inline-flex items-center gap-2 justify-center"
                      data-testid="btn-copy-gallery-link"
                    >
                      <LinkIcon size={13} /> Copia link gallery
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="card-flat p-6 bg-gray-50 border-l-4 border-[color:var(--nsa-orange)] md:sticky md:top-24">
                <span className="label-eyebrow text-[color:var(--nsa-orange)]">Partecipazione libera</span>
                <h3 className="font-display uppercase text-xl mt-2">Per informazioni</h3>
                <p className="text-sm text-gray-600 mt-3 leading-relaxed">
                  Non è richiesta iscrizione: puoi presentarti direttamente sul posto.
                  Per informazioni o dettagli, contattaci qui sotto.
                </p>
                <a href="#form-evento" className="mt-5 block btn-orange label-eyebrow text-center py-3">Contattaci</a>
                <button
                  onClick={copyEventLink}
                  className="mt-3 w-full border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-3 label-eyebrow text-sm transition-colors inline-flex items-center gap-2 justify-center"
                >
                  <LinkIcon size={14} /> Copia link evento
                </button>
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* Gallery */}
      {gallery.length > 0 && (
        <Section>
          <div id="gallery" className="scroll-mt-24 flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div className="flex items-center gap-3">
              <Images size={22} className="text-[color:var(--nsa-orange)]" />
              <div>
                <span className="label-eyebrow text-[color:var(--nsa-orange)]">Photo Gallery</span>
                <h2 className="font-display uppercase text-3xl md:text-4xl mt-1">Le foto dell'evento</h2>
              </div>
            </div>
            <button
              onClick={copyGalleryLink}
              className="self-start md:self-auto border border-gray-300 hover:border-[color:var(--nsa-orange)] hover:text-[color:var(--nsa-orange)] px-4 py-2.5 label-eyebrow text-xs transition-colors inline-flex items-center gap-2"
              data-testid="btn-copy-gallery-link-header"
            >
              <LinkIcon size={14} /> Copia link gallery
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
            {gallery.map((src, idx) => (
              <button
                key={`${src}-${idx}`}
                type="button"
                onClick={() => openLightbox(idx)}
                className="group relative aspect-square overflow-hidden bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[color:var(--nsa-orange)]"
                data-testid={`gallery-thumb-${idx}`}
              >
                <img
                  src={mediaUrl(src)}
                  alt={`Foto ${idx + 1}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* Form contatto (solo se evento non passato) */}
      {!event.is_past && (
        <Section tone="dark">
          <div id="form-evento" className="grid md:grid-cols-5 gap-10 md:gap-16 items-start">
            <div className="md:col-span-2 text-white">
              <span className="label-eyebrow text-[color:var(--nsa-orange)]">Per informazioni</span>
              <h2 className="font-display uppercase text-4xl md:text-5xl mt-3 leading-[0.95]">Contattaci<br />per l'evento</h2>
              <p className="mt-6 text-white/80 leading-relaxed max-w-md">
                Lasciaci i tuoi dati e la tua richiesta: ti ricontatteremo con tutte le informazioni.
              </p>
            </div>
            <div className="md:col-span-3">
              <ContactForm
                section="moto"
                defaultSubject={`${event.title} — ${event.date_label || event.date}`}
                submitLabel="Invia richiesta"
                tid="event-contact-form"
              />
            </div>
          </div>
        </Section>
      )}

      {/* Thank you banner (eventi passati) */}
      {event.is_past && (
        <Section tone="dark">
          <div className="text-center text-white max-w-3xl mx-auto py-6" data-testid="event-past-banner">
            <span className="label-eyebrow text-[color:var(--nsa-orange)] tracking-[0.3em]">Evento concluso</span>
            <h2 className="font-display uppercase text-4xl md:text-5xl mt-4 leading-[0.95]">
              Grazie di cuore<br />a tutti i partecipanti
            </h2>
            <p className="mt-6 text-white/80 leading-relaxed">
              Scorri la gallery qui sopra per cercarti e scaricare direttamente le tue foto.
              <br />
              Appuntamento al prossimo evento NSA Motors!
            </p>
            <Link
              to="/moto/eventi"
              className="mt-8 inline-flex items-center gap-2 label-eyebrow bg-[color:var(--nsa-orange)] text-white px-6 py-3 hover:bg-white hover:text-gray-900 transition-colors"
            >
              Vedi tutti gli eventi
            </Link>
          </div>
        </Section>
      )}

      {/* Lightbox */}
      {lightboxIdx >= 0 && gallery[lightboxIdx] && (
        <Lightbox
          src={gallery[lightboxIdx]}
          index={lightboxIdx}
          total={gallery.length}
          onClose={closeLightbox}
          onPrev={prevImg}
          onNext={nextImg}
          onDownload={() => downloadImage(gallery[lightboxIdx])}
        />
      )}

      {/* Press Release modal */}
      {showPressRelease && event.press_release && (
        <PressReleaseModal
          content={event.press_release}
          onClose={() => setShowPressRelease(false)}
        />
      )}
    </PageLayout>
  );
}


function PressReleaseModal({ content, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/90 flex items-start md:items-center justify-center p-0 md:p-6 overflow-y-auto"
      onClick={onClose}
      data-testid="press-release-modal"
    >
      <div
        className="bg-white w-full md:max-w-3xl md:my-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 flex items-center justify-between gap-4 px-6 md:px-10 py-4 border-b border-gray-200 bg-white z-10">
          <div className="flex items-center gap-2 text-[color:var(--nsa-orange)]">
            <FileText size={16} />
            <span className="label-eyebrow tracking-[0.2em] text-xs">Comunicato stampa</span>
          </div>
          <button
            onClick={onClose}
            className="bg-gray-100 hover:bg-gray-200 p-2 transition-colors"
            data-testid="press-release-close"
            aria-label="Chiudi"
          >
            <X size={18} />
          </button>
        </div>
        <div className="px-6 md:px-10 py-8 prose prose-lg max-w-none event-body">
          <ReactMarkdown
            components={{
              h2: (p) => <h2 className="font-display uppercase text-2xl md:text-3xl mt-2 mb-5 leading-tight" {...p} />,
              h3: (p) => <h3 className="font-display uppercase text-lg md:text-xl mt-8 mb-3 text-[color:var(--nsa-orange)]" {...p} />,
              p: (p) => <p className="text-base md:text-lg mb-5 text-gray-700 leading-relaxed" {...p} />,
              strong: (p) => <strong className="text-gray-900 font-semibold" {...p} />,
              blockquote: (p) => <blockquote className="my-6 pl-5 border-l-4 border-[color:var(--nsa-orange)] italic text-gray-700" {...p} />,
            }}
          >
            {content}
          </ReactMarkdown>
        </div>
      </div>
    </div>
  );
}


function Lightbox({ src, index, total, onClose, onPrev, onNext, onDownload }) {
  const [touchStart, setTouchStart] = useState(null);

  const onTouchStart = (e) => setTouchStart(e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const dx = e.changedTouches[0].clientX - touchStart;
    if (Math.abs(dx) > 40) {
      if (dx < 0) onNext();
      else onPrev();
    }
    setTouchStart(null);
  };

  return (
    <div
      className="fixed inset-0 z-[70] bg-black/95 flex items-center justify-center"
      onClick={onClose}
      data-testid="gallery-lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 md:px-8 py-4 z-10 bg-gradient-to-b from-black/70 to-transparent">
        <span className="text-white/80 text-sm label-eyebrow tracking-widest">{index + 1} / {total}</span>
        <div className="flex gap-2 md:gap-3">
          <button
            onClick={(e) => { e.stopPropagation(); onDownload(); }}
            className="bg-[color:var(--nsa-orange)] hover:bg-[color:var(--nsa-orange)]/90 text-white px-3 md:px-4 py-2 md:py-2.5 label-eyebrow text-xs md:text-sm inline-flex items-center gap-2 transition-colors"
            data-testid="lightbox-download"
          >
            <Download size={14} /> <span className="hidden sm:inline">Scarica foto</span><span className="sm:hidden">Scarica</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="bg-white/10 hover:bg-white/20 text-white p-2 md:p-2.5 transition-colors"
            data-testid="lightbox-close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Image */}
      <img
        src={mediaUrl(src)}
        alt={`Foto ${index + 1}`}
        className="max-h-[88vh] max-w-[94vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Prev/Next (hidden on small when swipe is enough) */}
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors"
        aria-label="Precedente"
      >
        <ChevronLeft size={24} />
      </button>
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white p-3 transition-colors"
        aria-label="Successivo"
      >
        <ChevronRight size={24} />
      </button>

      {/* Mobile bottom nav */}
      <div className="md:hidden absolute bottom-6 left-0 right-0 flex justify-center gap-3">
        <button onClick={(e) => { e.stopPropagation(); onPrev(); }} className="bg-white/10 hover:bg-white/20 text-white p-3"><ChevronLeft size={18} /></button>
        <button onClick={(e) => { e.stopPropagation(); onNext(); }} className="bg-white/10 hover:bg-white/20 text-white p-3"><ChevronRight size={18} /></button>
      </div>
    </div>
  );
}
